/* devicesListComponent.js */

"use strict";
const {get} = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./devicesListRenderer");
const PagingDeviceFeed = require("../../../views/iot_device_grid_view/PagingDeviceFeed");
const SiteCodeColumn = require("../../../views/iot_device_grid_view/column_renderers/site_code/SiteCodeComponent");

class DevicesListComponent extends EventEmitter {
    constructor(
        selectedDevices,
        DeviceEntity,
        userSession,
        licenseRestriction,
        modalUserFeedback,
        logger
    ) {
        super();
        this._selectedDevices = selectedDevices;
        this._deviceEntityDef = DeviceEntity;
        this._userSession = userSession;
        this._logger = logger;
        this._licenseRestriction = licenseRestriction;
        this._modalUserFeedback = modalUserFeedback;
        this.reject = this.reject.bind(this);
        this.resolve = this.resolve.bind(this);
        this._activeOrgId = get(this._userSession, "activeOrganization.id", "");
        this._gridKey = "copyFrom" + "_" + "iotDeviceExplorer";
        this._hasDiscoveredDevices = false;
        this._hasTopologyLicense = this._licenseRestriction.checkLicense("TOP");
        this._hasTopologyAccess =
            get(this._userSession, "_userEntity.authorizedActions", []).indexOf("topologyAccess") >=
            0;

        this.columns = {
            name: {
                label: "Device Name",
                width: 220,
                shown: true,
                priority: 1,
            },
            description: {
                label: "Description",
                shown: true,
                width: 300,
                priority: 2,
            },
            siteCodeName: {
                label: "Site Code Name",
                columnRendererComponent: SiteCodeColumn,
                entityColumn: true,
                shown: true,
                width: 160,
                priority: 3,
                context: {
                    hasTopologyLicense: this._hasTopologyLicense,
                    hasTopologyAccess: false,
                },
            },
            manufacturer: {
                label: "Manufacturer",
                shown: true,
                width: 220,
                priority: 4,
            },
            deviceType: {
                label: "Device Type",
                shown: true,
                width: 220,
                priority: 5,
            },
            model: {
                label: "Model",
                shown: true,
                width: 100,
                priority: 6,
            },
            osName: {
                label: "OS Name",
                shown: true,
                width: 220,
                priority: 7,
            },
            osFamily: {
                label: "OS Family",
                shown: true,
                width: 100,
                priority: 8,
            },
            osReleaseName: {
                label: "OS Release Name",
                shown: true,
                width: 100,
                priority: 9,
            },
            osVersion: {
                label: "OS Version",
                shown: true,
                width: 100,
                priority: 10,
            },
            machineArchitecture: {
                label: "Machine Architecture",
                shown: true,
                width: 150,
                priority: 11,
            },
            language: {
                label: "Language",
                shown: true,
                width: 100,
                priority: 12,
            },
        };
        this._multipleCombos = false;

        this._renderer = new Renderer(this);

        this._renderer.render();
    }

    get renderer() {
        return this._renderer;
    }

    reject(err) {
        this._context.reject(err);
    }
    resolve(action) {
        this._context.resolve(action);
    }

    // prettier-ignore
    set "$teve.ui.context" (context) { //jshint ignore:line
        this._context = context;
    }

    async whenModelIsReady() {
        try {
            await this._loadSelectedDeviceEntities();
            await this._loadDevicesFeed();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    async shutdown() {
        await this._renderer.shutdown();
        this._deviceEntityDef = undefined;
        this._userSession = undefined;
        this._logger = undefined;
        this._licenseRestriction = undefined;
        this._modalUserFeedback = undefined;
        this._activeOrgId = undefined;
        this._gridKey = undefined;
        this._hasTopologyLicense = undefined;
        this._hasTopologyAccess = undefined;
        this.columns = undefined;
        this._renderer = undefined;
        this._multipleCombos = undefined;
        this._hasDiscoveredDevices = undefined;
    }

    async _loadSelectedDeviceEntities() {
        try {
            const selectedDeviceEntities = await this._deviceEntityDef.loadDevices(
                this._activeOrgId,
                {
                    $and: [
                        {id: {$in: this._selectedDevices}},
                        {offlineDiscoveryStatus: "DISCOVERED"},
                    ],
                },
                {offlineDiscoveryStatus: true}
            );

            if (selectedDeviceEntities.length > 0) {
                this._hasDiscoveredDevices = true;
            }
        } catch (err) {
            Promise.reject(err);
        }
    }

    async _loadDevicesFeed() {
        try {
            const _filter = {};
            _filter.$userQuery = {};
            _filter.$systemQuery = _filter.$systemQuery || {};

            // Ensure $systemQuery.$and exists and is an array
            if (!_filter.$systemQuery.$and || !Array.isArray(_filter.$systemQuery.$and)) {
                _filter.$systemQuery.$and = [];
            }

            _filter.$systemQuery.$and.push({deviceRawCategory: "iot"});
            _filter.$systemQuery.$and.push({offlineDiscoveryStatus: "DISCOVERED"});
            _filter.$systemQuery.$and.push({id: {$nin: this._selectedDevices}});

            const selectedDevicesDetails = await this._deviceEntityDef.loadDevices(
                this._activeOrgId,
                {id: {$in: this._selectedDevices}},
                {edpValues: true}
            );

            const uniqueCombos = [];
            const seen = new Set();
            for (const device of selectedDevicesDetails) {
                const manufacturer = get(
                    device,
                    "edpValues.xdome.deviceIdDetails.manufacturer",
                    ""
                );
                const model = get(device, "edpValues.xdome.deviceIdDetails.model", "");
                const deviceType = get(device, "edpValues.xdome.deviceIdDetails.deviceType", "");
                const key = [manufacturer, model, deviceType].join("|");
                if (!seen.has(key)) {
                    seen.add(key);
                    uniqueCombos.push({
                        "edpValues.xdome.deviceIdDetails.manufacturer": manufacturer,
                        "edpValues.xdome.deviceIdDetails.model": model,
                        "edpValues.xdome.deviceIdDetails.deviceType": deviceType,
                    });
                }
            }

            // Add $and filter if there is one combo
            if (uniqueCombos.length === 1) {
                _filter.$systemQuery.$and.push({$and: uniqueCombos});
            }

            if (uniqueCombos.length === 1) {
                this._noDevicesMessage = `No discovered devices whose Manufacturer (${uniqueCombos[0]["edpValues.xdome.deviceIdDetails.manufacturer"]}), Model (${uniqueCombos[0]["edpValues.xdome.deviceIdDetails.model"]}), and Type (${uniqueCombos[0]["edpValues.xdome.deviceIdDetails.deviceType"]}) match the target devices.`;
                this._pagingDeviceFeed = new PagingDeviceFeed({
                    feedKey: this._gridKey + "_feed",
                    activeOrgId: this._activeOrgId,
                    entityDef: this._deviceEntityDef,
                    columnConfig: this.columns,
                    filter: _filter,
                    sort: {},
                    additionalFields: [],
                });

                await this._pagingDeviceFeed.whenFeedReady();
            } else {
                this._multipleCombos = true;
            }

            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    async _copyDeviceProperties(deviceId) {
        try {
            this.resolve({
                selectedDevice: deviceId,
                selectedDevices: this._selectedDevices,
            });
        } catch (err) {
            return Promise.reject(err, "Error loading Selected Device");
        }
    }

    cancelAction() {
        this.reject({userRejected: true});
    }
}

module.exports = DevicesListComponent;
